// A refusal of the API, from its problem+json document (RFC 9457): shown in plain French, with its cause.

export interface ProblemDocument {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: { field: string; message: string }[];
  [extra: string]: unknown;
}

export class ApiProblem extends Error {
  readonly status: number;
  readonly title: string;
  readonly detail: string | undefined;
  readonly code: string | undefined;
  readonly errors: { field: string; message: string }[];
  /** seconds before trying again (quota exceeded, 429) */
  readonly retryAfter: number | undefined;
  readonly document: ProblemDocument;

  constructor(status: number, document: ProblemDocument, retryAfter?: number) {
    const title = document.title || (status === 0 ? "Service injoignable" : `Erreur ${status}`);
    super(document.detail ? `${title} : ${document.detail}` : title);
    this.name = "ApiProblem";
    this.status = status;
    this.title = title;
    this.detail = document.detail;
    this.code = document.type?.split("/").pop();
    this.errors = Array.isArray(document.errors) ? document.errors : [];
    this.retryAfter = retryAfter;
    this.document = document;
  }

  /** The message for a person: title, cause, the invalid fields, when to try again. */
  get text(): string {
    const parts = [this.message];
    if (this.errors.length) parts.push(this.errors.map((e) => `${e.field} : ${e.message}`).join(" ; "));
    if (this.status === 429) parts.push(this.retryAfter ? `Réessayer dans ${this.retryAfter} s.` : "Réessayer dans un moment.");
    return parts.join(" — ");
  }
}

function retryAfterOf(response: Response): number | undefined {
  const h = response.headers.get("Retry-After");
  if (!h) return undefined;
  const s = Number(h);
  if (Number.isFinite(s)) return Math.max(0, Math.round(s));
  const at = Date.parse(h);
  return Number.isNaN(at) ? undefined : Math.max(0, Math.round((at - Date.now()) / 1000));
}

/** The problem of a failed response, its body already read (openapi-fetch) or still to read. */
export async function problemOf(response: Response, body?: unknown): Promise<ApiProblem> {
  let doc: ProblemDocument = {};
  if (body && typeof body === "object") doc = body as ProblemDocument;
  else if (body === undefined) {
    try {
      doc = (await response.clone().json()) as ProblemDocument;
    } catch {
      doc = {};
    }
  }
  return new ApiProblem(response.status, doc, retryAfterOf(response));
}

export function messageOf(e: unknown): string {
  if (e instanceof ApiProblem) return e.text;
  if (e instanceof TypeError) return "Service injoignable : vérifier la connexion.";
  if (e instanceof Error) return e.message;
  return String(e);
}
